import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { api, qs } from '../../lib/api.js'
import {
  Badge, Card, Empty, ErrorBox, Field, Modal, Page, Spinner, SubmitButton, useAsync, useConfirm, useToast
} from '../../components/Ui.jsx'
import { fmtDate, fmtStamp, lakh } from '../../lib/format.js'

const TONE = { new: 'warn', contacted: 'info', converted: 'good', closed: 'muted' }

// The inland belts most enquiries come from — the farmer never gives coordinates,
// and matching is useless without them.
const PRESETS = [
  { label: 'Bhimavaram', lat: 16.5449, lng: 81.5212 },
  { label: 'Eluru', lat: 16.7107, lng: 81.0952 },
  { label: 'Bapatla', lat: 15.9044, lng: 80.4675 },
  { label: 'Chirala', lat: 15.8236, lng: 80.3520 },
  { label: 'Nellore', lat: 14.4426, lng: 79.9865 },
  { label: 'Amalapuram', lat: 16.5786, lng: 82.0060 },
  { label: 'Kakinada', lat: 16.9891, lng: 82.2475 }
]

export default function Enquiries () {
  const [status, setStatus] = useState('open')
  const { data, error, loading, reload } = useAsync(() => api.get(`/operator/enquiries${qs({ status })}`), [status], { live: ['enquiries'] })
  const toast = useToast()
  const confirm = useConfirm()
  const navigate = useNavigate()
  const [converting, setConverting] = useState(null)
  const [coords, setCoords] = useState({ lat: '', lng: '' })
  const [busy, setBusy] = useState(false)

  const mark = async (enq, next) => {
    const ok = await confirm({
      title: `Mark ${enq.enquiryCode} as ${next}?`,
      body: next === 'closed'
        ? 'It drops off the open list. Do this only once the farmer has been dealt with.'
        : 'This is how the desk tracks who has already been rung back.',
      facts: [['Farmer', enq.name], ['Phone', enq.phone]],
      confirmLabel: `Mark ${next}`,
      tone: next === 'closed' ? 'danger' : ''
    })
    if (!ok) return
    try {
      const res = await api.post(`/operator/enquiries/${enq._id}/status`, { status: next })
      toast.ok(res.message)
      reload()
    } catch (err) { toast.error(err.message) }
  }

  const convert = async e => {
    e.preventDefault()
    setBusy(true)
    const ok = await confirm({
      title: `Add ${converting.name} to the farmer book?`,
      body: 'This creates a farmer record the desk can book against, and marks the enquiry converted.',
      facts: [['Phone', converting.phone], ['Pond', `${coords.lat}, ${coords.lng}`]],
      confirmLabel: 'Add farmer'
    })
    if (!ok) return
    try {
      const res = await api.post(`/operator/enquiries/${converting._id}/convert`, coords)
      toast.ok(res.message)
      setConverting(null)
      setCoords({ lat: '', lng: '' })
      reload()
      navigate(`/operator/match?farmerId=${res.farmer._id}`)
    } catch (err) {
      toast.error(err.message)
    } finally { setBusy(false) }
  }

  const rows = data?.enquiries || []

  return (
    <Page
      title="Farmer enquiries"
      subtitle="Call-back requests from the public directory. Nothing here is booked — ring the farmer, add them to the book, then match them a batch."
      actions={<a className="btn" href="/hatcheries" target="_blank" rel="noreferrer">Open the public directory ↗</a>}
    >
      {data && (
        <div className="grid three" style={{ marginBottom: 18 }}>
          <div className={`stat ${data.counts.new ? 'warn' : ''}`}>
            <div className="n">{data.counts.new}</div>
            <div className="k">Not yet called</div>
          </div>
          <div className="stat"><div className="n">{data.counts.contacted}</div><div className="k">Contacted</div></div>
          <div className="stat"><div className="n">{data.counts.converted}</div><div className="k">Converted to a booking</div></div>
        </div>
      )}

      <Card padded={false}>
        <div className="filterbar">
          <div className="chips">
            {[['open', 'Open'], ['new', 'Not called'], ['contacted', 'Contacted'], ['converted', 'Converted'], ['', 'All']]
              .map(([v, label]) => (
                <button key={label} className={`chip ${status === v ? 'on' : ''}`} onClick={() => setStatus(v)}>{label}</button>
              ))}
          </div>
          <div style={{ flex: 1 }} />
          <div className="muted small">{rows.length} enquir{rows.length === 1 ? 'y' : 'ies'}</div>
        </div>

        {loading
          ? <Spinner />
          : error
            ? <div className="body"><ErrorBox error={error} onRetry={reload} /></div>
            : (
              <div className="scroll-x">
                <table>
                  <thead>
                    <tr>
                      <th>Enquiry</th><th>Farmer</th><th>Asking about</th><th>Needs</th>
                      <th>Received</th><th>Status</th><th />
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map(e => (
                      <tr key={e._id}>
                        <td className="mono">{e.enquiryCode}</td>
                        <td>
                          <b>{e.name}</b>
                          <span className="t-sub">
                            <a href={`tel:${e.phone}`}>{e.phone}</a>
                            {[e.village, e.mandal, e.district].filter(Boolean).length > 0 &&
                              ` · ${[e.village, e.mandal, e.district].filter(Boolean).join(', ')}`}
                          </span>
                        </td>
                        <td>
                          {e.hatchery
                            ? <>{e.hatchery.name}<span className="t-sub mono">{e.batch?.batchCode || 'any batch'}</span></>
                            : <span className="muted">No hatchery in particular</span>}
                        </td>
                        <td>
                          {e.quantityLakh ? lakh(e.quantityLakh) : '—'}
                          <span className="t-sub">
                            {e.targetStockingDate ? `stocking ${fmtDate(e.targetStockingDate)}` : 'no date given'}
                            {e.pondSalinityPpt != null ? ` · ${e.pondSalinityPpt} ppt` : ''}
                          </span>
                        </td>
                        <td className="small">{fmtStamp(e.createdAt)}</td>
                        <td>
                          <Badge tone={TONE[e.status]}>{e.status}</Badge>
                          {e.handledBy && <span className="t-sub">{e.handledBy.displayName}</span>}
                        </td>
                        <td className="right nowrap">
                          {e.farmer
                            ? <Link className="btn sm primary" to={`/operator/match?farmerId=${e.farmer._id}`}>Match</Link>
                            : <button className="btn sm primary" onClick={() => setConverting(e)}>Add farmer</button>}
                          {' '}
                          {e.status === 'new' && <button className="btn sm" onClick={() => mark(e, 'contacted')}>Called</button>}
                          {e.status !== 'closed' && e.status !== 'converted' &&
                            <> <button className="btn sm" onClick={() => mark(e, 'closed')}>Close</button></>}
                        </td>
                      </tr>
                    ))}
                    {!rows.length && <tr><td colSpan={7}><Empty>No enquiries in this view.</Empty></td></tr>}
                  </tbody>
                </table>
              </div>
              )}
      </Card>

      {rows.some(e => e.notes) && (
        <Card title="What farmers wrote">
          {rows.filter(e => e.notes).slice(0, 8).map(e => (
            <div key={e._id} style={{ marginBottom: 14 }}>
              <div className="small"><b>{e.name}</b> <span className="mono muted">{e.enquiryCode}</span></div>
              <div className="muted small">“{e.notes}”</div>
            </div>
          ))}
        </Card>
      )}

      {converting && (
        <Modal title={`Add ${converting.name} to the farmer book`} onClose={() => setConverting(null)}>
          <form onSubmit={convert}>
            <p className="muted small" style={{ marginTop: 0 }}>
              Ask where the pond is while you have them on the phone. Distance ranking does not work
              without coordinates, so pick the nearest town if you cannot get exact ones.
            </p>
            <div className="kv" style={{ marginBottom: 16 }}>
              <div><div className="k">Phone</div><div className="v">{converting.phone}</div></div>
              <div><div className="k">Place given</div><div className="v">{[converting.village, converting.mandal, converting.district].filter(Boolean).join(', ') || '—'}</div></div>
            </div>
            <div className="formgrid">
              <Field label="Pond latitude" required>
                <input type="number" step="0.0001" value={coords.lat} onChange={e => setCoords({ ...coords, lat: e.target.value })} required />
              </Field>
              <Field label="Pond longitude" required>
                <input type="number" step="0.0001" value={coords.lng} onChange={e => setCoords({ ...coords, lng: e.target.value })} required />
              </Field>
            </div>
            <div className="chips" style={{ marginBottom: 16 }}>
              <span className="muted small" style={{ alignSelf: 'center', marginRight: 4 }}>Nearest town:</span>
              {PRESETS.map(p => (
                <button type="button" key={p.label} className="chip" onClick={() => setCoords({ lat: p.lat, lng: p.lng })}>
                  {p.label}
                </button>
              ))}
            </div>
            <div className="btnrow">
              <SubmitButton busy={busy} type="submit">Add farmer & go to matching</SubmitButton>
              <button type="button" className="btn" onClick={() => setConverting(null)}>Cancel</button>
            </div>
          </form>
        </Modal>
      )}
    </Page>
  )
}
